import React, {useState, Fragment} from "react";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {useParams} from "react-router";
import dayjs from "dayjs";
import writeXlsxFile from "write-excel-file";
import {unescape} from "es-toolkit/compat";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCircle} from "@fortawesome/pro-regular-svg-icons/faCircle";
import {faCircleDot} from "@fortawesome/pro-regular-svg-icons/faCircleDot";
import {faInfoCircle} from "@fortawesome/free-solid-svg-icons";
import {faExclamationCircle} from "@fortawesome/pro-regular-svg-icons/faExclamationCircle";

import Alert from "@/components/Global/Alert/Alert";
import Button from "@/components/Global/Button/Button";
import Modal from "@/components/Global/Modal/Modal";
import RenderIf from "@/components/Global/RenderIf/RenderIf";
import {useResults} from "@/hooks/useResults/useResults";
import {getMyFormPdf, getMyFormPdfV2, getPdf} from "@/features/results";
import {generateChartImage} from "@/helpers/generateChartImage.js";
import {generateChartImageV2} from "@/helpers/generateChartImageV2.js";

import {config} from "@/config";
import styles from "./ExportResults.module.scss";
import {selectIsOnline} from "@/features/appSettings/appSettingsSelectors";
import {selectUserDetails} from "@/features/user/userSelectors";

export default function ExportResults({modalId, isFemaleResult, useMyFormV2 = false}) {
  const dispatch = useDispatch();
  const isOnline = useSelector(selectIsOnline);
  const customerId = useSelector(selectUserDetails, shallowEqual)?.ID || 0;
  const [exportSelection, setExportSelection] = useState(0);
  const [specificTest, setSpecificTest] = useState(0);
  const [allTestDataOption, setAllTestDataOption] = useState(3);
  const [exportFileType, setExportFileType] = useState(0);
  const [pdfReady, setPdfReady] = useState(false);
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [downloadExcelSuccessfully, setDownloadExcelSuccessfully] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [hasFailed, setHasFailed] = useState(false);

  let {orderId} = useParams();
  orderId = parseInt(orderId);

  const {allResults, latestResult} = useResults();

  const resetState = () => {
    setExportSelection(0);
    setSpecificTest(0);
    setAllTestDataOption(3);
    setExportFileType(0);
    setPdfReady(false);
    setLoadingPDF(false);
    setPdfUrl("");
  };

  const fetchPDFs = () => {
    let resultType = exportSelection;
    let dataType = allTestDataOption;
    let orderId = specificTest;
    if (orderId == "") orderId = 0;
    if (resultType == "") resultType = 3;
    if (dataType == "") dataType = 0;
    setLoadingPDF(true);
    dispatch(getPdf({resultType, dataType, orderId})).then(({payload: response}) => {
      setPdfUrl(response.env);
      setPdfReady(true);
      setLoadingPDF(false);
    });
  };

  const getFormScoreChart = score => {
    const chartDataBg = document.getElementById("chart-data-background");
    const chartData = document.getElementById("chart-data");

    const newCanvas = document.createElement("canvas");
    const ctx = newCanvas.getContext("2d");
    const width = chartDataBg.clientWidth;
    const height = chartDataBg.clientHeight;

    newCanvas.width = width;
    newCanvas.height = height;

    [chartDataBg, chartData]?.forEach(n => {
      ctx.beginPath();
      ctx.drawImage(n, 0, 0, width, height);
    });

    const fontSize = (height / 60).toFixed(2);
    ctx.font = fontSize + "em Verdana";
    ctx.textBaseline = "middle";
    ctx.fillStyle = `${config.websiteId == 1 ? "black" : "#D4D5D8"}`;

    const text = `${score}`;
    const textX = Math.round((width - ctx.measureText(text).width) / 2);
    const textY = height / 2;
    ctx.fillText(text, textX, textY);

    ctx.font = 2 + "em Verdana";
    ctx.textBaseline = "middle";
    ctx.fillStyle = `${config.websiteId == 1 ? "black" : "#D4D5D8"}`;

    const percentage = "%";
    const percentageX = Math.round(width - ctx.measureText(percentage).width / 2 - textX * 0.9);
    const percentageY = height / 2 - ctx.measureText(percentage).width / 1.5;
    ctx.fillText(percentage, percentageX, percentageY);

    const myFormScore = "MYFORM SCORE";
    const myFormScoreX = Math.round(width / 2 - ctx.measureText(myFormScore).width / 2);
    const myFormScoreY = height / 2 + ctx.measureText(textY).width;
    ctx.fillText(myFormScore, myFormScoreX, myFormScoreY);

    return newCanvas.toDataURL();
  };

  const fetchMyFormPDF = () => {
    const data = allResults.find(result => result.orderId == orderId)?.reportV1;
    const dataV2 = allResults.find(result => result.orderId == orderId)?.reportV2;

    if (useMyFormV2) {
      const payload = {
        allHormonesChartImage: generateChartImageV2({data: dataV2, type: "allHormones"}),
        progesteroneChartImage: generateChartImageV2({data: dataV2, type: "progesterone"}),
        oestradiolChartImage: generateChartImageV2({data: dataV2, type: "oestradiol"}),
        fshChartImage: generateChartImageV2({data: dataV2, type: "fsh"}),
        lhChartImage: generateChartImageV2({data: dataV2, type: "lh"}),
        formScore: generateChartImageV2({data: dataV2, type: "formScore"}),
        customerId,
        orderId,
      };

      dispatch(getMyFormPdfV2(payload))
        .then(({payload: response}) => {
          if (response.error) {
            setHasFailed(true);
            setPdfReady(false);
            setPdfUrl("");

            setTimeout(() => {
              setHasFailed(false);
            }, 5000);

            return;
          }

          setPdfUrl(response.env);
          setPdfReady(true);
          setLoadingPDF(false);
          setHasFailed(false);
        })
        .finally(() => {
          setLoadingPDF(false);
        });
      return;
    }

    const progesteroneData = {
      day1Status: data.sample1_progesteroneStatus,
      day2Status: data.sample2_progesteroneStatus,
      sample1Day: data.sample1_day,
      sample1Result: data.sample1_progesteroneResult,
      sample2Day: data.sample2_day,
      sample2Result: data.sample2_progesteroneResult,
      commentary: data.commentary_progesteroneCurve,
      measurement: data.sample1_progesteroneUnits,
      bottomCurve: data.curve_progesterone_bottomPercentile.slice(0, -1),
      meanCurve: data.curve_progesterone_mean.slice(0, -1),
      personalCurve: data.curve_progesterone_personal.slice(0, -1),
      topCurve: data.curve_progesterone_topPercentile.slice(0, -1),
    };

    const oestradiolData = {
      sample1Day: data.sample1_day,
      sample2Day: data.sample2_day,
      sample1Result: data.sample1_oestradiolResult,
      sample2Result: data.sample2_oestradiolResult,
      day1Status: data.sample1_oestradiolStatus,
      day2Status: data.sample2_oestradiolStatus,
      commentary: data.commentary_oestradiolCurve,
      measurement: data.sample1_oestradiolUnits,
      bottomCurve: data.curve_oestradiol_bottomPercentile.slice(0, -1),
      meanCurve: data.curve_oestradiol_mean.slice(0, -1),
      personalCurve: data.curve_oestradiol_personal.slice(0, -1),
      topCurve: data.curve_oestradiol_topPercentile.slice(0, -1),
    };

    const fshData = {
      sample1Day: data.sample1_day,
      sample2Day: data.sample2_day,
      sample1Result: data.sample1_fshResult,
      sample2Result: data.sample2_fshResult,
      day1Status: data.sample1_fshStatus,
      day2Status: data.sample2_fshStatus,
      commentary: data.commentary_fshCurve,
      measurement: data.sample1_fshUnits,
      bottomCurve: data.curve_fsh_bottomPercentile.slice(0, -1),
      meanCurve: data.curve_fsh_mean.slice(0, -1),
      personalCurve: data.curve_fsh_personal.slice(0, -1),
      topCurve: data.curve_fsh_topPercentile.slice(0, -1),
    };

    const lhData = {
      sample1Day: data.sample1_day,
      sample2Day: data.sample2_day,
      sample1Result: data.sample1_lhResult,
      sample2Result: data.sample2_lhResult,
      day1Status: data.sample1_lhStatus,
      day2Status: data.sample2_lhStatus,
      commentary: data.commentary_lhCurve,
      measurement: data.sample1_oestradiolUnits,
      bottomCurve: data.curve_lh_bottomPercentile.slice(0, -1),
      meanCurve: data.curve_lh_mean.slice(0, -1),
      personalCurve: data.curve_lh_personal.slice(0, -1),
      topCurve: data.curve_lh_topPercentile.slice(0, -1),
    };

    const oestradiolBase64 = generateChartImage({data: oestradiolData, width: 630, height: 250});
    const progesteroneBase64 = generateChartImage({data: progesteroneData, width: 630, height: 250});
    const FSHBase64 = generateChartImage({data: fshData, width: 630, height: 250});
    const LHBase64 = generateChartImage({data: lhData, width: 630, height: 250});
    const formScore = getFormScoreChart(data.formScore);

    const payload = {
      FSHBase64,
      LHBase64,
      formScore,
      oestradiolBase64,
      orderId,
      progesteroneBase64,
    };

    setLoadingPDF(true);

    dispatch(getMyFormPdf(payload))
      .then(({payload: response}) => {
        setPdfUrl(response.env);
        setPdfReady(true);
      })
      .finally(() => {
        setLoadingPDF(false);
      });
  };

  const fetchExcel = () => {
    setLoadingExcel(true);
    let objects = [];
    if (exportSelection == 3 && allTestDataOption == 0) {
      objects = latestResult.biomarkers;
    }

    if (exportSelection == 3 && allTestDataOption == 1) {
      objects = [];
      allResults.map(item => item.biomarkers.map(item => objects.push(item)));
    }

    if (exportSelection == 2) {
      const filteredResults = allResults?.filter(result => {
        return result.orderId == specificTest;
      });
      objects = filteredResults[0].biomarkers;
    }
    const schema = [
      {
        column: "Biomarker",
        type: String,
        value: result => result.biomarkerName ?? "",
        width: 30,
      },
      {
        column: "Order",
        type: String,
        value: result => result.orderReference ?? "",
        width: 15,
      },
      {
        column: "Test",
        type: String,
        value: result => result.testName ?? "",
        width: 30,
      },
      {
        column: "Result",
        type: String,
        value: result => result.biomarkerResultScore.toString() ?? "",
        width: 10,
      },
      {
        column: "Normal",
        type: String,
        value: result => {
          const {
            biomarkerSufficientValue,
            biomarkerHighValue,
            biomarkerHideBorderlines,
            biomarkerHighestValueP,
            biomarkerToxicValueP,
          } = result;

          if (
            (biomarkerHideBorderlines == 1 || biomarkerHideBorderlines == 0) &&
            biomarkerToxicValueP == 0 &&
            biomarkerHighestValueP == 0
          ) {
            return `${biomarkerSufficientValue} <`;
          }

          if (biomarkerHideBorderlines == 0 && biomarkerSufficientValue == 0 && biomarkerHighestValueP != 0) {
            return `< ${biomarkerHighValue} `;
          }

          return biomarkerSufficientValue !== "" && biomarkerHighValue !== ""
            ? `${biomarkerSufficientValue} - ${biomarkerHighValue}`
            : "";
        },
        width: 15,
      },
      {
        column: "Unit",
        type: String,
        value: result => result.biomarkerUnit ?? "",
        width: 30,
      },
      {
        column: "Date",
        type: String,
        value: result => result.resultDate ?? "",
        width: 30,
      },
      {
        column: "Lab",
        type: String,
        value: result => result.labName ?? "",
        width: 30,
      },
    ];

    writeXlsxFile(objects, {
      schema,
      fileName: dayjs().format("DD/MM/YYYY") + "-results.xlsx",
      headerStyle: {
        color: "#000000",
        fontWeight: "bold",
      },
    }).finally(() => {
      setDownloadExcelSuccessfully(true);
      setLoadingExcel(false);

      setTimeout(() => {
        setDownloadExcelSuccessfully(false);
      }, 3000);
    });
  };

  return (
    <Modal id={modalId} variant="bottom" onClose={() => resetState()}>
      <div className="mb-3 px-3">
        <div className={`${styles.modalClose} text-end`}>
          <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
        </div>
        <h2 className="my-0">Export Results</h2>
        <RenderIf isTrue={!isOnline}>
          <div className="row mt-3">
            <div className="col-12">
              <Alert variant="info">
                <h3>
                  <FontAwesomeIcon icon={faInfoCircle} /> Info
                </h3>
                <p className="mb-0">You are offline, please connect to the internet to export results.</p>
              </Alert>
            </div>
          </div>
        </RenderIf>

        <RenderIf isTrue={!isFemaleResult}>
          <div className="row">
            <div className="col-12 mt-3">
              <h3 className={`${styles.defaultTextColor} my-2 py-1`}>
                <strong>What would you like to export?</strong>
              </h3>
            </div>
            <div className="col-6 mb-3">
              <Button
                onClick={() => {
                  setExportSelection(2);
                  setAllTestDataOption(3);
                  setSpecificTest(0);
                }}
                variant="selector"
                type="button"
                disabled={!isOnline}
                className="w-100 h-100 py-3 d-flex justify-content-center align-items-center gap-2">
                {exportSelection == 2 ? <FontAwesomeIcon icon={faCircleDot} /> : <FontAwesomeIcon icon={faCircle} />}{" "}
                Specific Test
              </Button>
            </div>
            <div className="col-6 mb-3">
              <Button
                onClick={() => {
                  setExportSelection(3);
                  setAllTestDataOption(3);
                  setSpecificTest(0);
                }}
                variant="selector"
                type="button"
                disabled={!isOnline}
                className="w-100 h-100 py-3 d-flex justify-content-center align-items-center gap-2">
                {exportSelection == 3 ? <FontAwesomeIcon icon={faCircleDot} /> : <FontAwesomeIcon icon={faCircle} />}{" "}
                All Tests
              </Button>
            </div>
          </div>
          <div className="row">
            <RenderIf isTrue={exportSelection == 2}>
              <div className="col-12">
                <h3 className={`${styles.defaultTextColor} my-2 py-1`}>
                  <strong>Which test would you like to export?</strong>
                </h3>
              </div>
              <div className="col-12 mb-3">
                <select
                  className="form-select"
                  value={specificTest || ""}
                  disabled={!isOnline}
                  onChange={event => setSpecificTest(event.target.value)}
                  aria-label="Default select example">
                  <option value="" disabled>
                    Select a test...
                  </option>
                  {allResults.map((result, index) => (
                    <Fragment key={index}>
                      {result.isFemaleHormoneTest == 0 && (
                        <option value={result.orderId}>
                          {unescape(result.testName)} - {dayjs(result.orderCompleteDate).format("DD/MM/YYYY")}
                        </option>
                      )}
                    </Fragment>
                  ))}
                </select>
              </div>
            </RenderIf>

            <RenderIf isTrue={exportSelection == 3}>
              <div className="col-12">
                <h3 className={`${styles.defaultTextColor} my-2 py-1`}>
                  <strong>How much data do you want?</strong>
                </h3>
              </div>
              <div className="col-6 mb-3">
                <Button
                  onClick={() => setAllTestDataOption(0)}
                  variant="selector"
                  type="button"
                  disabled={!isOnline}
                  className="w-100 h-100 py-3 d-flex justify-content-center align-items-center gap-2">
                  {allTestDataOption == 0 ? (
                    <FontAwesomeIcon icon={faCircleDot} />
                  ) : (
                    <FontAwesomeIcon icon={faCircle} />
                  )}{" "}
                  Latest Results
                </Button>
              </div>
              <div className="col-6 mb-3">
                <Button
                  onClick={() => setAllTestDataOption(1)}
                  variant="selector"
                  type="button"
                  disabled={!isOnline}
                  className="w-100 h-100 py-3 d-flex justify-content-center align-items-center gap-2">
                  {allTestDataOption == 1 ? (
                    <FontAwesomeIcon icon={faCircleDot} />
                  ) : (
                    <FontAwesomeIcon icon={faCircle} />
                  )}{" "}
                  All Results
                </Button>
              </div>
            </RenderIf>
          </div>

          <div className="row">
            <div className="col-12">
              <h3 className={`${styles.defaultTextColor} mt-3 mb-2 py-1`}>
                <strong>What file type?</strong>
              </h3>
            </div>
            <div className="col-6 mb-3">
              <Button
                onClick={() => setExportFileType("xlsx")}
                variant="selector"
                type="button"
                disabled={!isOnline}
                className="w-100 h-100 py-3 d-flex justify-content-center align-items-center gap-2">
                {exportFileType == "xlsx" ? (
                  <FontAwesomeIcon icon={faCircleDot} />
                ) : (
                  <FontAwesomeIcon icon={faCircle} />
                )}{" "}
                Export to Excel
              </Button>
            </div>
            <div className="col-6 mb-3">
              <Button
                onClick={() => setExportFileType("pdf")}
                variant="selector"
                type="button"
                disabled={!isOnline}
                className="w-100 h-100 py-3 d-flex justify-content-center align-items-center gap-2">
                {exportFileType == "pdf" ? <FontAwesomeIcon icon={faCircleDot} /> : <FontAwesomeIcon icon={faCircle} />}{" "}
                Export to PDF
              </Button>
            </div>
          </div>

          <div className="row mt-2">
            <div className="col-12">
              <RenderIf
                isTrue={
                  exportFileType == "pdf" &&
                  exportSelection != 0 &&
                  (allTestDataOption == 0 || allTestDataOption == 1 || specificTest != 0)
                }>
                <RenderIf isTrue={!pdfReady}>
                  <Button
                    onClick={() => {
                      isOnline && fetchPDFs();
                    }}
                    variant="primary"
                    type="button"
                    className="w-100 py-3 d-flex justify-content-center gap-2"
                    isLoading={loadingPDF}
                    disabled={loadingPDF || !isOnline}
                    loadingText="Loading...">
                    Download
                  </Button>
                </RenderIf>

                <RenderIf isTrue={pdfReady}>
                  <Button
                    onClick={() => {
                      fetchPDFs();
                    }}
                    variant="success"
                    type="button"
                    disabled={!isOnline}
                    className="w-100 py-3 d-flex justify-content-center gap-2"
                    target="_blank"
                    href={pdfUrl}>
                    PDF Ready. Click here to open
                  </Button>
                </RenderIf>
              </RenderIf>

              <RenderIf
                isTrue={
                  exportFileType == "xlsx" &&
                  exportSelection != 0 &&
                  (allTestDataOption == 0 || allTestDataOption == 1 || specificTest != 0)
                }>
                <Button
                  onClick={() => {
                    !downloadExcelSuccessfully && isOnline && fetchExcel();
                  }}
                  variant={downloadExcelSuccessfully ? "success" : "primary"}
                  type="button"
                  className="w-100 py-3 d-flex justify-content-center gap-2"
                  isLoading={loadingExcel}
                  disabled={loadingExcel || !isOnline}
                  loadingText="Loading...">
                  {downloadExcelSuccessfully ? "Excel downloaded Successfully" : "Download"}
                </Button>
              </RenderIf>

              <Button
                variant="link-default"
                type="button"
                className="text-dark w-100 mt-3 py-3 d-flex justify-content-center gap-2"
                data-bs-dismiss="modal"
                aria-label="Close">
                Cancel
              </Button>
            </div>
          </div>
        </RenderIf>

        <RenderIf isTrue={isFemaleResult}>
          <p className="lh-base mt-3">
            This report, including any scores, graphs and commentary, is for information purposes only. It is not
            intended as a substitute for medical diagnosis. If you have concerns about your health, please talk to your
            GP.
          </p>

          <div className="row mt-4">
            <div className="col-12">
              <RenderIf isTrue={!pdfReady && !hasFailed}>
                <Button
                  onClick={() => {
                    isOnline && fetchMyFormPDF();
                  }}
                  variant="primary"
                  type="button"
                  className="w-100 py-3 d-flex justify-content-center gap-2"
                  isLoading={loadingPDF}
                  disabled={loadingPDF || !isOnline}
                  loadingText="Loading...">
                  Download PDF
                </Button>
              </RenderIf>

              <RenderIf isTrue={!pdfReady && hasFailed}>
                <Button
                  variant="danger"
                  type="button"
                  className="w-100 d-block text-center py-3"
                  target="_blank"
                  href={pdfUrl}>
                  <FontAwesomeIcon icon={faExclamationCircle} className="me-2" />
                  PDF Failed to load. Please try again later.
                </Button>
              </RenderIf>

              <RenderIf isTrue={pdfReady && !hasFailed}>
                <Button
                  variant="success"
                  type="button"
                  disabled={!isOnline}
                  className="w-100 d-block text-center py-3"
                  target="_blank"
                  href={pdfUrl}>
                  PDF Ready. Click here to open
                </Button>
              </RenderIf>
            </div>
            <div className="col-12">
              <Button variant="link-default" className="w-100 mt-3 py-3" data-bs-dismiss="modal" aria-label="Close">
                Cancel
              </Button>
            </div>
          </div>
        </RenderIf>
      </div>
    </Modal>
  );
}
